import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type DateRange = { fromDate: string; toDate: string };
export type RangePreset = "3m" | "6m" | "12m" | "ytd" | "custom";

const PRESET_OPTIONS: { value: Exclude<RangePreset, "custom">; key: string }[] =
	[
		{ value: "3m", key: "preset3m" },
		{ value: "6m", key: "preset6m" },
		{ value: "12m", key: "preset12m" },
		{ value: "ytd", key: "presetYtd" },
	];

export function computePresetRange(
	preset: Exclude<RangePreset, "custom">,
): DateRange {
	const now = new Date();
	const end = new Date(
		Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			23,
			59,
			59,
			999,
		),
	);
	const start = new Date(end.getTime());
	switch (preset) {
		case "3m":
			start.setUTCMonth(start.getUTCMonth() - 3);
			break;
		case "6m":
			start.setUTCMonth(start.getUTCMonth() - 6);
			break;
		case "12m":
			start.setUTCMonth(start.getUTCMonth() - 12);
			break;
		case "ytd":
			start.setUTCMonth(0, 1);
			start.setUTCHours(0, 0, 0, 0);
			break;
	}
	return { fromDate: start.toISOString(), toDate: end.toISOString() };
}

export function isoToDateInputValue(iso: string): string {
	const d = new Date(iso);
	const year = d.getUTCFullYear();
	const month = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function dateInputValueToIso(value: string, endOfDay: boolean): string {
	const [year, month, day] = value.split("-").map(Number);
	if (!year || !month || !day) {
		return new Date().toISOString();
	}
	const d = endOfDay
		? new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999))
		: new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
	return d.toISOString();
}

interface RangeFiltersProps {
	preset: RangePreset;
	range: DateRange;
	validationError?: string | null;
	onPresetChange: (next: RangePreset) => void;
	onFromChange: (value: string) => void;
	onToChange: (value: string) => void;
	/** Optional left-aligned slot, e.g. a granularity toggle, rendered above the preset chips on mobile and to the left on md+. */
	leftSlot?: React.ReactNode;
	/** Stable id prefix used for input ids and `data-testid` so existing tests/automation can keep targeting the same nodes. */
	idPrefix?: string;
}

export function RangeFilters({
	preset,
	range,
	validationError,
	onPresetChange,
	onFromChange,
	onToChange,
	leftSlot,
	idPrefix = "range-filters",
}: RangeFiltersProps) {
	const { t } = useTranslation();

	return (
		<Card>
			<CardContent className="p-3 md:p-4 space-y-3">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
					{leftSlot && <div>{leftSlot}</div>}
					<div
						className="flex flex-wrap gap-1 md:gap-0 md:join"
						role="tablist"
						data-testid={`${idPrefix}-preset-toggle`}
					>
						{PRESET_OPTIONS.map((option) => (
							<button
								key={option.value}
								type="button"
								role="tab"
								aria-selected={preset === option.value}
								className={`btn btn-sm md:join-item ${
									preset === option.value ? "btn-primary" : "btn-ghost"
								}`}
								onClick={() => onPresetChange(option.value)}
							>
								{t(`orgManagement.financialAnalysis.filters.${option.key}`)}
							</button>
						))}
						<button
							type="button"
							role="tab"
							aria-selected={preset === "custom"}
							className={`btn btn-sm md:join-item ${
								preset === "custom" ? "btn-primary" : "btn-ghost"
							}`}
							onClick={() => onPresetChange("custom")}
						>
							{t("orgManagement.financialAnalysis.filters.presetCustom")}
						</button>
					</div>
				</div>

				{preset === "custom" && (
					<>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							<div>
								<Label
									htmlFor={`${idPrefix}-from-date`}
									className="text-xs text-base-content/60"
								>
									{t("orgManagement.financialAnalysis.filters.fromDate")}
								</Label>
								<Input
									id={`${idPrefix}-from-date`}
									type="date"
									value={isoToDateInputValue(range.fromDate)}
									onChange={(e) => onFromChange(e.target.value)}
									className="mt-1"
									data-testid={`${idPrefix}-from-date`}
								/>
							</div>
							<div>
								<Label
									htmlFor={`${idPrefix}-to-date`}
									className="text-xs text-base-content/60"
								>
									{t("orgManagement.financialAnalysis.filters.toDate")}
								</Label>
								<Input
									id={`${idPrefix}-to-date`}
									type="date"
									value={isoToDateInputValue(range.toDate)}
									onChange={(e) => onToChange(e.target.value)}
									className="mt-1"
									data-testid={`${idPrefix}-to-date`}
								/>
							</div>
						</div>
						{validationError && (
							<p className="text-error text-sm">{validationError}</p>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}
